import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CaretRight, SpinnerGap } from '@phosphor-icons/react';
import { Footer } from './LegalPages';
import axios from 'axios';
import DOMPurify from 'dompurify';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Fallback content registry (for migration period) ─────────────────
const GUIDE_CONTENT = {};

// Helper to register guide pages
const registerGuide = (lang, category, slug, data) => {
  GUIDE_CONTENT[`${lang}/${category}/${slug}`] = data;
};

// ─── Tower DE (Fallback) ──────────────────────────────────────────────
registerGuide('de', 'tarot', 'turm', {
  title: 'Der Turm – Tarot Bedeutung',
  metaTitle: 'Der Turm – Tarot Bedeutung: Zerstörung als Neuanfang | ONIMA',
  metaDescription: 'Was zusammenbricht, macht Platz. Der Turm im Tarot: Bedeutung in Liebe, Beruf & Leben. Entdecke deine persönliche Deutung.',
  canonical: 'https://onima.cc/guide/de/tarot/turm',
  hreflang: [
    { lang: 'de', href: 'https://onima.cc/guide/de/tarot/turm' },
    { lang: 'en', href: 'https://onima.cc/guide/en/tarot/tower' },
    { lang: 'x-default', href: 'https://onima.cc/guide/en/tarot/tower' },
  ],
  ogImage: 'https://onima.cc/images/tarot/tower.jpg',
  breadcrumbs: [
    { label: 'Home', href: '/' },
    { label: 'Guide', href: '/guide' },
    { label: 'Tarot', href: '/guide/de/tarot' },
    { label: 'Der Turm', href: '/guide/de/tarot/turm' },
  ],
  hook: 'Alles, was du gebaut hast, brennt. Das ist keine Strafe. Das ist eine Befreiung.',
  sections: [
    {
      heading: 'Die Karte, die niemand sehen will',
      content: `Ein Blitz. Ein brennender Turm. Fallende Körper.

Wenn du diese Karte ziehst, zuckt etwas in dir zusammen. Das ist normal. Das ist sogar gut.

Der Turm ist nicht hier, um dir zu schaden. Er ist hier, um dir zu zeigen, was bereits bröckelt — was du vielleicht selbst zusammenhältst mit Kraft, die du für etwas Besseres brauchst.`
    },
    {
      heading: 'Grundbedeutung',
      content: `Der Turm ist der sechzehnte Arkana des Tarot.

Aber vergiss die Zahlen. Vergiss die Lehrbücher. Diese Karte ist der Moment, in dem die Illusion zerbricht.

Du hast etwas aufgebaut. Eine Beziehung, eine Karriere, ein Selbstbild. Es sah stabil aus. Von außen.

Der Turm sagt: Das Fundament war morsch.

Das ist keine Grausamkeit. Das ist Gnade. Denn in einem Gebäude zu leben, das kurz vor dem Einsturz steht — ist schlimmer, als seinen Fall zu erleben.`
    },
    {
      heading: 'Aufrechte Position',
      content: `Du hast etwas gebaut. Beziehungen, Karriere, Weltbild. Es schien stabil. Von außen.

Der Turm sagt: Es war auf Sand gebaut.

Jobverlust. Trennung. Eine Erkenntnis, die alles verändert. Es tut weh. So soll es sein. Denn nur so verstehst du, dass du frei bist.

Was jetzt passiert, ist nicht das Ende. Es ist der Moment, bevor etwas Echtes beginnen kann.`,
      bullets: [
        'Plötzliche Veränderungen außerhalb deiner Kontrolle',
        'Zerstörung alter Strukturen',
        'Wahrheit, die ans Licht kommt',
        'Befreiung von dem, was dich einschränkte',
      ]
    },
    {
      heading: 'Umgekehrte Position',
      content: `Manchmal steht der Turm auf dem Kopf — und fällt trotzdem nicht.

Das ist keine Erleichterung. Das ist eine Warnung.

Du weißt, dass etwas nicht stimmt. Du spürst die Risse. Aber du hältst fest, weil die Alternative Angst macht.

Der umgekehrte Turm fragt: Wie lange noch? Und zu welchem Preis?

Manchmal ist der kontrollierte Abriss besser als der unkontrollierte Einsturz.`,
      bullets: [
        'Widerstand gegen unvermeidliche Veränderungen',
        'Angst loszulassen, was nicht mehr funktioniert',
        'Innere Spannung, die sich aufbaut',
        'Chance, Stein für Stein abzubauen, bevor alles von selbst einstürzt',
      ]
    },
    {
      heading: 'In der Liebe',
      content: `In einer Beziehung bedeutet der Turm Erschütterung.

Vielleicht eine Wahrheit, die ans Licht kommt. Vielleicht ein Streit, der längst überfällig war. Vielleicht die Erkenntnis, dass ihr verschiedene Wege geht.

Das klingt hart. Aber hier ist die Wahrheit: Eine Beziehung, die den Turm braucht, war nicht so stabil, wie sie schien.

Was nach dem Einsturz bleibt — das ist echt. Das ist der Boden, auf dem etwas Neues wachsen kann.`
    },
    {
      heading: 'Im Beruf',
      content: `Im Beruf zeigt der Turm: Dein sicherer Job ist nicht so sicher. Dein Projekt steht auf wackeligen Beinen. Dein Plan funktioniert nicht.

Das ist kein Scheitern. Das ist Information.

Der Turm im Beruf fragt: Was würdest du tun, wenn du wüsstest, dass dieses Gebäude morgen einstürzt?

Die Antwort zeigt dir, was als Nächstes kommt.`
    },
    {
      heading: 'Als Tageskarte',
      content: `Der Turm als Tageskarte sagt nicht: Heute geht alles schief.

Er sagt: Heute ist ein Tag für Ehrlichkeit.

Schau hin, wo du Kompromisse machst, die dich kosten. Wo du Strukturen aufrechterhältst, die dich einschränken. Wo du dich selbst belügst.

Heute ist kein Tag für große Pläne. Es ist ein Tag zum Aufräumen.`
    },
  ],
  faq: [
    {
      question: 'Ist der Turm eine "schlechte" Karte?',
      answer: 'Nein. Er zeigt Zerstörung — aber nur von dem, was nicht echt war. Was danach bleibt, ist stabiler.',
    },
    {
      question: 'Was bedeutet der Turm in einer Ja/Nein-Frage?',
      answer: 'Meist "Nein" oder "Noch nicht." Die Karte zeigt, dass die Situation instabil ist und Veränderung braucht.',
    },
    {
      question: 'Wie überlebt man eine Turm-Phase?',
      answer: 'Nicht wehren. Das Alte gehen lassen. Fokus auf das, was bleibt — das ist das Echte.',
    },
  ],
  relatedCards: [
    { combo: 'Turm + Tod', meaning: 'Vollständige Transformation, Ende und Neugeburt' },
    { combo: 'Turm + Stern', meaning: 'Nach der Zerstörung kommt Hoffnung und Heilung' },
    { combo: 'Turm + Rad des Schicksals', meaning: 'Schicksalhafte Wendung außerhalb deiner Kontrolle' },
    { combo: 'Turm + Teufel', meaning: 'Befreiung von Sucht oder toxischer Situation' },
    { combo: 'Turm + Liebende', meaning: 'Beziehungskrise, Moment der Wahrheit' },
  ],
  cta: {
    text: 'Bereit zu erfahren, was der Turm dir persönlich sagt?',
    button: 'Deine persönliche Deutung',
    href: '/',
  },
  alternateSlug: { lang: 'en', href: '/guide/en/tarot/tower' },
});

// ─── Tower EN (Fallback) ──────────────────────────────────────────────
registerGuide('en', 'tarot', 'tower', {
  title: 'The Tower – Tarot Meaning',
  metaTitle: 'The Tower – Tarot Meaning: Destruction as New Beginning | ONIMA',
  metaDescription: 'What collapses makes space. The Tower in Tarot: meaning in love, career & life. Discover your personal interpretation.',
  canonical: 'https://onima.cc/guide/en/tarot/tower',
  hreflang: [
    { lang: 'en', href: 'https://onima.cc/guide/en/tarot/tower' },
    { lang: 'de', href: 'https://onima.cc/guide/de/tarot/turm' },
    { lang: 'x-default', href: 'https://onima.cc/guide/en/tarot/tower' },
  ],
  ogImage: 'https://onima.cc/images/tarot/tower.jpg',
  breadcrumbs: [
    { label: 'Home', href: '/' },
    { label: 'Guide', href: '/guide' },
    { label: 'Tarot', href: '/guide/en/tarot' },
    { label: 'The Tower', href: '/guide/en/tarot/tower' },
  ],
  hook: 'Everything you built is burning. This is not punishment. This is liberation.',
  sections: [
    {
      heading: 'The Card Nobody Wants to See',
      content: `Lightning. A burning tower. Falling bodies.

When you draw this card, something inside you flinches. That's normal. That's even good.

The Tower isn't here to harm you. It's here to show you what's already crumbling — what you might be holding together with strength you need for something better.`
    },
    {
      heading: 'Core Meaning',
      content: `The Tower is the sixteenth Major Arcana.

But forget the numbers. Forget the textbooks. This card is the moment the illusion shatters.

You built something. A relationship, a career, a self-image. It looked stable. From the outside.

The Tower says: The foundation was rotten.

This isn't cruelty. This is mercy. Because living in a building about to collapse is worse than experiencing its fall.`
    },
    {
      heading: 'Upright Position',
      content: `You built something. Relationships, career, worldview. It seemed stable. From the outside.

The Tower says: It was built on sand.

Job loss. Breakup. A realization that changes everything. It hurts. It's supposed to. Because only then do you understand you're free.

What's happening now isn't the end. It's the moment before something real can begin.`,
      bullets: [
        'Sudden changes beyond your control',
        'Destruction of old structures',
        'Truth coming to light',
        'Liberation from what constrained you',
      ]
    },
    {
      heading: 'Reversed Position',
      content: `Sometimes the Tower stands upside down — and still doesn't fall.

This isn't relief. This is a warning.

You know something's wrong. You feel the cracks. But you hold on because the alternative scares you.

The reversed Tower asks: How much longer? And at what cost?

Sometimes controlled demolition is better than uncontrolled collapse.`,
      bullets: [
        'Resistance to inevitable changes',
        'Fear of letting go of what no longer works',
        'Inner tension building up',
        'Chance to dismantle stone by stone before everything collapses on its own',
      ]
    },
    {
      heading: 'In Love',
      content: `In a relationship, the Tower means upheaval.

Perhaps a truth coming to light. Perhaps an overdue argument. Perhaps the realization you're going different ways.

That sounds harsh. But here's the truth: A relationship that needs the Tower wasn't as stable as it seemed.

What remains after the collapse — that's real. That's the ground where something new can grow.`
    },
    {
      heading: 'In Career',
      content: `In career, the Tower shows: Your secure job isn't so secure. Your project stands on shaky legs. Your plan isn't working.

This isn't failure. This is information.

The Tower in career asks: What would you do if you knew this building collapses tomorrow?

The answer shows you what comes next.`
    },
    {
      heading: 'As Daily Card',
      content: `The Tower as daily card doesn't say: Today everything goes wrong.

It says: Today is a day for honesty.

Look where you're making compromises that cost you. Where you're maintaining structures that constrain you. Where you're lying to yourself.

Today isn't a day for big plans. It's a day for clearing out.`
    },
  ],
  faq: [
    {
      question: 'Is the Tower a "bad" card?',
      answer: "No. It shows destruction — but only of what wasn't real. What remains afterward is more stable.",
    },
    {
      question: 'What does the Tower mean in a yes/no question?',
      answer: 'Usually "No" or "Not now." The card indicates the situation is unstable and changes are needed.',
    },
    {
      question: 'How do you survive a Tower phase?',
      answer: "Don't resist. Let the old go. Focus on what remains — that's what's real.",
    },
  ],
  relatedCards: [
    { combo: 'Tower + Death', meaning: 'Complete transformation, ending and rebirth' },
    { combo: 'Tower + Star', meaning: 'After destruction comes hope and healing' },
    { combo: 'Tower + Wheel of Fortune', meaning: 'Fateful turn beyond your control' },
    { combo: 'Tower + Devil', meaning: 'Liberation from addiction or toxic situation' },
    { combo: 'Tower + Lovers', meaning: 'Relationship crisis, moment of truth' },
  ],
  cta: {
    text: 'Ready to discover what the Tower says to you personally?',
    button: 'Get Your Personal Reading',
    href: '/',
  },
  alternateSlug: { lang: 'de', href: '/guide/de/tarot/turm' },
});

// ─── Render helpers ─────────────────────────────────────────────────
const renderMarkdown = (text) => {
  // Very simple markdown: **bold**, *italic*, newlines
  return text.split('\n\n').map((para, i) => {
    const html = para
      .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em class="text-starlight/80 italic">$1</em>')
      .replace(/\n/g, '<br/>');
    return (
      <p
        key={i}
        className="text-muted-foreground leading-relaxed mb-4 text-base"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
      />
    );
  });
};

// ─── FAQ Schema JSON-LD ─────────────────────────────────────────────
const buildFaqSchema = (faq) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faq.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

// ─── Transform API response to component format ─────────────────────
const transformApiGuide = (apiData) => {
  return {
    title: apiData.title,
    metaTitle: apiData.meta_title,
    metaDescription: apiData.meta_description,
    canonical: apiData.canonical,
    hreflang: apiData.hreflang || [],
    ogImage: apiData.og_image,
    breadcrumbs: apiData.breadcrumbs || [],
    hook: apiData.hook,
    sections: apiData.sections || [],
    faq: apiData.faq || [],
    relatedCards: [], // Will be loaded separately
    cta: {
      text: apiData.lang === 'de' 
        ? 'Bereit zu erfahren, was diese Karte dir persönlich sagt?'
        : apiData.lang === 'ru'
        ? 'Готов узнать, что эта карта говорит лично тебе?'
        : apiData.lang === 'uk'
        ? 'Готовий дізнатися, що ця карта говорить особисто тобі?'
        : 'Ready to discover what this card says to you personally?',
      button: apiData.lang === 'de' 
        ? 'Deine persönliche Deutung'
        : apiData.lang === 'ru'
        ? 'Твоё личное толкование'
        : apiData.lang === 'uk'
        ? 'Твоє особисте тлумачення'
        : 'Get Your Personal Reading',
      href: '/',
    },
    alternateSlug: apiData.alternates?.length > 0 
      ? { lang: apiData.alternates[0].lang, href: `/guide/${apiData.alternates[0].lang}/${apiData.category}/${apiData.alternates[0].slug}` }
      : null,
    // Store raw data for related articles
    _raw: apiData
  };
};

// ─── Loading Skeleton ───────────────────────────────────────────────
const GuideSkeleton = () => (
  <div className="min-h-screen bg-abyss text-foreground font-body animate-pulse">
    <header className="border-b border-white/5 sticky top-0 z-40 bg-abyss/90 backdrop-blur-md">
      <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
        <div className="w-8 h-8 bg-white/10 rounded-full"></div>
        <div className="h-4 bg-white/10 rounded w-48"></div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="h-10 bg-white/10 rounded w-3/4 mb-6"></div>
      <div className="h-6 bg-white/10 rounded w-full mb-4"></div>
      <div className="h-6 bg-white/10 rounded w-5/6 mb-8"></div>
      {[1,2,3].map(i => (
        <div key={i} className="mb-8">
          <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-white/5 rounded w-full mb-2"></div>
          <div className="h-4 bg-white/5 rounded w-full mb-2"></div>
          <div className="h-4 bg-white/5 rounded w-4/5"></div>
        </div>
      ))}
    </main>
  </div>
);

// ─── Not Found Component ────────────────────────────────────────────
const GuideNotFound = ({ lang }) => (
  <div className="min-h-screen bg-abyss text-foreground font-body flex flex-col items-center justify-center px-4">
    <h1 className="font-heading text-3xl text-starlight mb-4">
      {lang === 'de' ? 'Seite nicht gefunden' : 
       lang === 'ru' ? 'Страница не найдена' :
       lang === 'uk' ? 'Сторінка не знайдена' :
       'Page Not Found'}
    </h1>
    <p className="text-muted-foreground mb-8 text-center max-w-md">
      {lang === 'de' ? 'Diese Seite existiert nicht oder wurde verschoben.' :
       lang === 'ru' ? 'Эта страница не существует или была перемещена.' :
       lang === 'uk' ? 'Ця сторінка не існує або була переміщена.' :
       'This page does not exist or has been moved.'}
    </p>
    <Link
      to="/"
      className="text-starlight hover:text-starlight/80 underline underline-offset-4"
    >
      {lang === 'de' ? 'Zurück zur Startseite' :
       lang === 'ru' ? 'Вернуться на главную' :
       lang === 'uk' ? 'Повернутися на головну' :
       'Back to Home'}
    </Link>
  </div>
);

// ─── Guide Page Component ───────────────────────────────────────────
const GuidePage = () => {
  const { lang, category, slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Load guide from API (with fallback to hardcoded)
  useEffect(() => {
    const loadGuide = async () => {
      setLoading(true);
      setNotFound(false);

      try {
        // Try API first
        const response = await axios.get(`${API}/api/guides/${lang}/${category}/${slug}`);
        const apiGuide = transformApiGuide(response.data);
        setGuide(apiGuide);

        // Load related articles
        try {
          const relatedRes = await axios.get(`${API}/api/guides/related/${lang}/${category}/${slug}?limit=5`);
          setRelatedArticles(relatedRes.data || []);
        } catch (e) {
          // Ignore related articles errors
        }
      } catch (err) {
        // Fallback to hardcoded content
        const key = `${lang}/${category}/${slug}`;
        const fallback = GUIDE_CONTENT[key];
        
        if (fallback) {
          setGuide(fallback);
        } else {
          setNotFound(true);
        }
      }
      
      setLoading(false);
    };

    loadGuide();
  }, [lang, category, slug]);

  // SEO: Set meta tags
  useEffect(() => {
    if (!guide) return;
    
    document.title = guide.metaTitle;

    const setMeta = (attr, metaKey, content) => {
      let el = document.querySelector(`meta[${attr}="${metaKey}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, metaKey);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', guide.metaDescription);
    setMeta('property', 'og:title', guide.metaTitle);
    setMeta('property', 'og:description', guide.metaDescription);
    setMeta('property', 'og:url', guide.canonical);
    setMeta('property', 'og:type', 'article');
    if (guide.ogImage) setMeta('property', 'og:image', guide.ogImage);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', guide.canonical);

    // Hreflang
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    guide.hreflang?.forEach(({ lang: hLang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hLang);
      link.setAttribute('href', href);
      document.head.appendChild(link);
    });

    // FAQ Schema
    if (guide.faq?.length > 0) {
      let faqScript = document.getElementById('faq-schema');
      if (!faqScript) {
        faqScript = document.createElement('script');
        faqScript.id = 'faq-schema';
        faqScript.type = 'application/ld+json';
        document.head.appendChild(faqScript);
      }
      faqScript.textContent = JSON.stringify(buildFaqSchema(guide.faq));
    }

    // Cleanup
    return () => {
      document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
      const faqScript = document.getElementById('faq-schema');
      if (faqScript) faqScript.remove();
    };
  }, [guide]);

  if (loading) {
    return <GuideSkeleton />;
  }

  if (notFound || !guide) {
    return <GuideNotFound lang={lang} />;
  }

  return (
    <div className="min-h-screen bg-abyss text-foreground font-body">
      {/* Header */}
      <header className="border-b border-white/5 sticky top-0 z-40 bg-abyss/90 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link
            to="/"
            className="text-muted-foreground hover:text-starlight transition-colors"
            data-testid="guide-back-btn"
          >
            <ArrowLeft size={24} weight="bold" />
          </Link>
          <nav className="flex items-center gap-1 text-sm text-muted-foreground overflow-x-auto" data-testid="guide-breadcrumbs">
            {guide.breadcrumbs?.map((crumb, i, arr) => (
              <React.Fragment key={i}>
                <Link
                  to={crumb.href}
                  className={`whitespace-nowrap ${i === arr.length - 1 ? 'text-starlight/80' : 'hover:text-starlight/70'}`}
                >
                  {crumb.label}
                </Link>
                {i < arr.length - 1 && <CaretRight size={14} className="flex-shrink-0 mx-1 text-muted-foreground/50" />}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-heading text-3xl sm:text-4xl lg:text-5xl text-starlight tracking-wide mb-6"
          data-testid="guide-title"
        >
          {guide.title}
        </motion.h1>

        {/* Hook */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-lg sm:text-xl text-muted-foreground/80 italic leading-relaxed mb-10 pl-4 border-l-2 border-starlight/30"
          data-testid="guide-hook"
        >
          {guide.hook}
        </motion.div>

        {/* Sections */}
        {guide.sections?.map((section, i) => (
          <motion.section
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.05 }}
            className="mb-10"
            data-testid={`guide-section-${i}`}
          >
            <h2 className="font-heading text-xl sm:text-2xl text-starlight/90 tracking-wide mb-4">
              {section.heading}
            </h2>
            <div className="prose prose-invert prose-p:text-muted-foreground">
              {renderMarkdown(section.content)}
            </div>
            {section.bullets && (
              <ul className="mt-4 space-y-2 pl-4">
                {section.bullets.map((bullet, j) => (
                  <li key={j} className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-starlight/60 mt-1.5">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.section>
        ))}

        {/* FAQ Accordion */}
        {guide.faq?.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-12"
            data-testid="guide-faq"
          >
            <h2 className="font-heading text-xl sm:text-2xl text-starlight/90 tracking-wide mb-6">
              {lang === 'de' ? 'Häufige Fragen' : 
               lang === 'ru' ? 'Частые вопросы' :
               lang === 'uk' ? 'Часті питання' :
               'Frequently Asked Questions'}
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {guide.faq.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border border-white/5 rounded-xl bg-white/[0.02] overflow-hidden"
                  data-testid={`guide-faq-${i}`}
                >
                  <AccordionTrigger className="px-5 py-4 text-left text-sm font-medium text-foreground/90 hover:text-starlight hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-4 text-muted-foreground text-sm leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.section>
        )}

        {/* Related Cards / Articles */}
        {(guide.relatedCards?.length > 0 || relatedArticles.length > 0) && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="mb-12"
            data-testid="guide-related"
          >
            <h2 className="font-heading text-xl sm:text-2xl text-starlight/90 tracking-wide mb-6">
              {lang === 'de' ? 'Verwandte Artikel' : 
               lang === 'ru' ? 'Связанные статьи' :
               lang === 'uk' ? "Пов'язані статті" :
               'Related Articles'}
            </h2>
            <div className="grid gap-3">
              {/* Related articles from API */}
              {relatedArticles.map((article, i) => (
                <Link
                  key={article.id}
                  to={`/guide/${lang}/${article.category}/${article.slug}`}
                  className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:border-starlight/20 transition-colors"
                  data-testid={`guide-related-article-${i}`}
                >
                  <span className="text-starlight/70 font-heading text-sm">
                    {article.title}
                  </span>
                </Link>
              ))}
              {/* Fallback related cards */}
              {guide.relatedCards?.map((card, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:border-starlight/20 transition-colors"
                  data-testid={`guide-related-${i}`}
                >
                  <span className="text-starlight/70 font-heading text-sm whitespace-nowrap min-w-[160px]">
                    {card.combo}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    — {card.meaning}
                  </span>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* CTA */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-16 text-center"
          data-testid="guide-cta"
        >
          <div className="bg-starlight/5 border border-starlight/20 rounded-2xl p-8 sm:p-12">
            <h2 className="font-heading text-xl sm:text-2xl text-starlight tracking-wide mb-4">
              {guide.cta?.text}
            </h2>
            <Link
              to={guide.cta?.href || '/'}
              className="inline-block bg-starlight/10 border border-starlight/50 text-starlight
                         px-8 py-4 rounded-xl font-heading tracking-widest uppercase text-sm
                         hover:bg-starlight/20 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]
                         transition-all duration-300 active:scale-95"
              data-testid="guide-cta-btn"
            >
              {guide.cta?.button}
            </Link>
          </div>
        </motion.section>

        {/* Alternate language link */}
        {guide.alternateSlug && (
          <div className="mb-12 text-center">
            <Link
              to={guide.alternateSlug.href}
              className="text-sm text-muted-foreground/50 hover:text-starlight/70 transition-colors"
              data-testid="guide-alternate-lang"
            >
              {guide.alternateSlug.lang === 'en' ? 'Read in English' : 
               guide.alternateSlug.lang === 'de' ? 'Auf Deutsch lesen' :
               guide.alternateSlug.lang === 'ru' ? 'Читать на русском' :
               guide.alternateSlug.lang === 'uk' ? 'Читати українською' :
               `Read in ${guide.alternateSlug.lang.toUpperCase()}`}
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default GuidePage;
